import { useCallback, useEffect, useState } from 'react'
import { updateCustomerPaymentDetails, UserID } from '@library/api-payment'
import { getCheckoutComPublicKey } from '@library/environment/isomorphic'
import { loadCheckoutScript } from '../utils/loadCheckoutScript'
import { DataFrame, CardConfirmationRedirectTo } from '../../../enums'

import type {
  FramesAppendedProps,
  FrameCardTokenizedEvent,
  FrameValidationChangedEvent,
  FrameCardValidationChangedEvent,
} from '../Frames'

declare global {
  interface Window {
    Frames: FramesAppendedProps
  }
}

const errorMessage = {
  cardNumber: 'Please enter a valid card number',
  cvv: 'Please enter a valid CVV code',
  expiryDate: 'Please enter a valid expiry date',
}

const initialState = {
  hasCheckoutScriptLoaded: false,
  hasCheckoutFrameStarted: false,
  error: undefined,
  inputNameOnCardError: false,
  inputExpiryDateError: false,
  inputCvvError: false,
  isCheckoutFormValid: false,
  isCheckoutFormSubmitting: false,
  cardConfirmationRedirectURL: undefined,
}

const useCheckoutComFrame = (userId: UserID) => {
  const [hasCheckoutScriptLoaded, setHasCheckoutScriptLoaded] = useState<boolean>(
    initialState.hasCheckoutScriptLoaded,
  )
  const [hasCheckoutFrameStarted, setHasCheckoutFrameStarted] = useState<boolean>(
    initialState.hasCheckoutFrameStarted,
  )
  const [error, setError] = useState<string | undefined>(initialState.error)
  const [inputNameOnCardError, setInputNameOnCardError] = useState<boolean>(
    initialState.inputNameOnCardError,
  )
  const [inputExpiryDateError, setInputExpiryDateError] = useState<boolean>(
    initialState.inputExpiryDateError,
  )
  const [inputCvvError, setInputCvvError] = useState<boolean>(initialState.inputCvvError)
  const [isCheckoutFormValid, setIsCheckoutFormValid] = useState<boolean>(
    initialState.isCheckoutFormValid,
  )
  const [isCheckoutFormSubmitting, setIsCheckoutFormSubmitting] = useState<boolean>(
    initialState.isCheckoutFormSubmitting,
  )
  const [cardConfirmationRedirectURL, setCardConfirmationRedirectURL] = useState<
    string | undefined
  >(initialState.cardConfirmationRedirectURL)

  const reset = () => {
    setHasCheckoutScriptLoaded(initialState.hasCheckoutScriptLoaded)
    setHasCheckoutFrameStarted(initialState.hasCheckoutFrameStarted)
    setError(initialState.error)
    setInputNameOnCardError(initialState.inputNameOnCardError)
    setInputExpiryDateError(initialState.inputExpiryDateError)
    setInputCvvError(initialState.inputCvvError)
    setIsCheckoutFormValid(initialState.isCheckoutFormValid)
    setIsCheckoutFormSubmitting(initialState.isCheckoutFormSubmitting)
    setCardConfirmationRedirectURL(initialState.cardConfirmationRedirectURL)
  }

  const tryLoadCheckoutScript = async () => {
    setHasCheckoutScriptLoaded(false)

    try {
      await loadCheckoutScript(window.document)

      setHasCheckoutScriptLoaded(true)
    } catch (error: Error | unknown) {
      setError('An error occurred, please try again or contact customer service.')
    }
  }

  const handleCardValidationChanged = ({ isValid }: FrameCardValidationChangedEvent) => {
    setIsCheckoutFormValid(isValid)
  }

  const handleFrameValidationChanged = useCallback((event: FrameValidationChangedEvent) => {
    const isInvalid = event.isEmpty || !event.isValid

    switch (event.element) {
      case 'card-number':
        return setInputNameOnCardError(isInvalid)
      case 'expiry-date':
        return setInputExpiryDateError(isInvalid)
      case 'cvv':
        return setInputCvvError(isInvalid)
    }
  }, [])

  const handleSubmitPaymentMethods = useCallback(
    async (cardToken: string) => {
      try {
        const location = window.location.origin

        const response = await updateCustomerPaymentDetails({
          user_id: userId,
          is_default: 1,
          type: 'card',
          card: {
            payment_provider: 'checkout',
            active: 1,
            card_token: cardToken,
          },
          '3ds': true,
          success_url: `${location}${CardConfirmationRedirectTo.SUCCESS}`,
          failure_url: `${location}${CardConfirmationRedirectTo.FAILURE}`,
        })

        setCardConfirmationRedirectURL(response.redirectLink)
      } catch (error: Error | unknown) {
        if (error instanceof Error && error.message.includes('status=422')) {
          setError(
            'Please check your Card Number, Expiry Date, Security Number and Billing Address are all correct.',
          )
        } else {
          setError('An error occurred, please try again or contact customer service.')
        }
      } finally {
        setIsCheckoutFormSubmitting(false)
      }
    },
    [userId],
  )

  const handleCardTokenized = useCallback(
    (response: FrameCardTokenizedEvent) => {
      handleSubmitPaymentMethods(response.token)
      window.Frames.enableSubmitForm()
    },
    [handleSubmitPaymentMethods],
  )

  const handleCardTokenizationFailed = () => {
    setIsCheckoutFormSubmitting(false)
    setError('Please check your Card Number, Expiry Date and Security Number are all correct.')
  }

  const handleSubmit = async (inputNameOnCard: string | undefined) => {
    setError(undefined)
    setIsCheckoutFormSubmitting(true)

    if (inputNameOnCard) {
      window.Frames.cardholder.name = inputNameOnCard
    }

    window.Frames.submitCard()
  }

  const initializeFrames = useCallback(() => {
    try {
      const config = {
        publicKey: getCheckoutComPublicKey(),
        style: {
          base: {
            fontSize: '15px',
            lineHeight: '24px',
            padding: '0 0.75rem',
            display: 'flex',
            alignItems: 'center',
            minHeight: '3rem',
            height: '3rem',
            letterSpacing: 0,
            fontFamily: '"AxiformaBook",Helvetica,sans-serif',
            color: '#333D47',
          },
          placeholder: {
            base: {
              opacity: 0,
            },
          },
        },
        cardNumber: {
          frameSelector: `[data-frames=${DataFrame.CARD_NUMBER}]`,
        },
        expiryDate: {
          frameSelector: `[data-frames=${DataFrame.EXPIRY_DATE}]`,
        },
        cvv: {
          frameSelector: `[data-frames=${DataFrame.CVV}]`,
        },
        localization: {
          cardNumberPlaceholder: 'Card number',
          expiryMonthPlaceholder: 'MM',
          expiryYearPlaceholder: 'YY',
          cvvPlaceholder: 'CVV',
        },
        cardValidationChanged: handleCardValidationChanged,
        frameValidationChanged: handleFrameValidationChanged,
        cardTokenized: handleCardTokenized,
        cardTokenizationFailed: handleCardTokenizationFailed,
        ready: () => {
          setHasCheckoutFrameStarted(true)
        },
      }

      if (window.Frames) {
        window.Frames.init(config)
      } else {
        throw Error()
      }
    } catch (error: Error | unknown) {
      setError('An error occurred, please try again or contact customer service.')
    }
  }, [handleCardTokenized, handleFrameValidationChanged, setHasCheckoutFrameStarted])

  useEffect(() => {
    if (!hasCheckoutScriptLoaded) {
      tryLoadCheckoutScript()
    } else if (!hasCheckoutFrameStarted && userId) {
      initializeFrames()
    }
  }, [userId, hasCheckoutScriptLoaded, hasCheckoutFrameStarted, initializeFrames])

  return {
    cardConfirmationRedirectURL,
    error,
    formValidationError: {
      cardNumber: inputNameOnCardError ? errorMessage.cardNumber : false,
      cvv: inputCvvError ? errorMessage.cvv : false,
      expiryDate: inputExpiryDateError ? errorMessage.expiryDate : false,
    },
    hasCheckoutFrameStarted,
    isCheckoutFormValid,
    isCheckoutFormSubmitting,
    onSubmitCheckout: handleSubmit,
    onSubmitCheckoutCardTokenized: handleCardTokenized,
    onSubmitCheckoutCardTokenizationFailed: handleCardTokenizationFailed,
    reset,
  }
}

export { useCheckoutComFrame }
